import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { PlayerScaffold, PlayerFooter } from '@/components/player/PlayerScaffold';
import { TabStrip, type Tab } from '@/components/player/TabStrip';
import { Question } from '@/components/quiz/Question';
import { OptionState, type OptionCss } from '@/components/quiz/types';
import type { EducateModule } from '@/hooks/useEducateModule';
import type { DiagnosticContent, EducateInteraction, QuizQuestion } from '@/types/educate';
import { OPTION_LETTERS } from '@/constants/ui';
import {
  clearPlayerProgress,
  loadPlayerProgress,
  savePlayerProgress
} from '@/utils/playerProgress';

interface DiagnosticSaved {
  step: number;
  furthest: number;
  tested: number[];
  pickedCheck: number | null;
  checkSubmitted: boolean;
}

const clampStep = (value: unknown) =>
  typeof value === 'number' ? Math.min(3, Math.max(0, value)) : 0;

const STEPS = ['Brief', 'Investigate', 'Root cause', 'Generalise'] as const;

const CHECK_OPTION_CSS: OptionCss = {
  base: 'flex w-full items-start gap-3 rounded-xl border px-5 py-4 text-left text-[15px] leading-relaxed transition disabled:cursor-default',
  state: {
    [OptionState.DEFAULT]: 'border-slate-200 bg-white text-slate-700 hover:border-brand-400',
    [OptionState.SELECTED]: 'border-brand-500 bg-brand-50 font-medium text-brand-900',
    [OptionState.CORRECT]: 'border-emerald-500 bg-emerald-50 text-emerald-900',
    [OptionState.INCORRECT]: 'border-red-400 bg-red-50 text-red-600',
    [OptionState.MUTED]: 'border-slate-200 bg-white text-slate-400'
  }
};

export function DiagnosticPlayer({ module }: { module: EducateModule }) {
  const { storyline, units, finishModule } = module;
  const content = storyline?.content as DiagnosticContent | undefined;
  const showLogo = storyline?.uiContent.uiConfig?.showKnolskapeLogo ?? true;

  const hypotheses = content?.hypotheses ?? [];
  const rootIndex = hypotheses.findIndex((h) => h.isRootCause);

  const saved = useMemo(
    () => loadPlayerProgress<DiagnosticSaved>(storyline?.storylineId),
    [storyline?.storylineId]
  );

  const [step, setStep] = useState(() => clampStep(saved.step));
  const [furthest, setFurthest] = useState(() => clampStep(saved.furthest));
  const [tested, setTested] = useState<Set<number>>(
    () => new Set(Array.isArray(saved.tested) ? saved.tested : [])
  );
  const [pickedCheck, setPickedCheck] = useState<number | null>(() =>
    typeof saved.pickedCheck === 'number' ? saved.pickedCheck : null
  );
  const [checkSubmitted, setCheckSubmitted] = useState(() => Boolean(saved.checkSubmitted));

  useEffect(() => {
    savePlayerProgress<DiagnosticSaved>(storyline?.storylineId, {
      step,
      furthest,
      tested: [...tested],
      pickedCheck,
      checkSubmitted
    });
  }, [storyline?.storylineId, step, furthest, tested, pickedCheck, checkSubmitted]);

  if (!content) return null;

  const rootIsolated = rootIndex >= 0 && tested.has(rootIndex);
  const hasCheck = Boolean(content.check && content.check.options?.length);
  const onGeneralise = step === 3;

  const canContinue = step === 1 ? rootIsolated : onGeneralise ? !hasCheck || checkSubmitted : true;
  const generaliseNeedsSubmit = onGeneralise && hasCheck && !checkSubmitted;

  const goToStep = (next: number) => {
    setStep(next);
    setFurthest((prev) => Math.max(prev, next));
  };

  const tabs: Tab[] = STEPS.map((label, index) => ({
    id: String(index),
    label,
    state:
      index === step
        ? 'active'
        : index < furthest || index < step
          ? 'done'
          : index <= furthest
            ? 'default'
            : 'locked'
  }));

  const finish = () => {
    clearPlayerProgress(storyline?.storylineId);
    const interactions: EducateInteraction[] =
      hasCheck && content.check
        ? [
            {
              unitId: 'check',
              kind: 'quiz',
              answer: pickedCheck ?? -1,
              isCorrect: pickedCheck === content.check.correctIndex
            }
          ]
        : [];
    finishModule(
      units.map((unit) => unit.id),
      interactions
    );
  };

  return (
    <PlayerScaffold
      eyebrow="Diagnostic"
      title={storyline?.uiContent.title ?? ''}
      showLogo={showLogo}
      setting="System under test"
      aside={
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
            rootIsolated ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${rootIsolated ? 'bg-emerald-500' : 'bg-slate-400'}`}
          />
          {rootIsolated
            ? 'Root cause isolated'
            : `Diagnosing · ${tested.size} of ${hypotheses.length} tested`}
        </span>
      }
      bodyTestId="diagnostic-screen"
      nav={<TabStrip tabs={tabs} onSelect={(id) => goToStep(Number(id))} />}
      footer={
        <PlayerFooter
          left={
            <button
              type="button"
              onClick={() => setStep((prev) => Math.max(0, prev - 1))}
              disabled={step <= 0}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 disabled:opacity-40"
            >
              <ArrowLeft size={16} /> Back
            </button>
          }
          center={
            step === 1 && !rootIsolated
              ? `Keep testing — ${tested.size} of ${hypotheses.length} checked, root cause not yet isolated`
              : generaliseNeedsSubmit
                ? `${pickedCheck === null ? 'Choose an answer' : 'Submit to see the answer'} · Step 4 of 4`
                : `Step ${step + 1} of 4`
          }
          right={
            generaliseNeedsSubmit ? (
              <button
                type="button"
                onClick={() => setCheckSubmitted(true)}
                disabled={pickedCheck === null}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
              >
                Submit answer
              </button>
            ) : onGeneralise ? (
              <button
                type="button"
                onClick={finish}
                disabled={!canContinue}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
              >
                Finish
              </button>
            ) : (
              <button
                type="button"
                onClick={() => goToStep(step + 1)}
                disabled={!canContinue}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-40"
              >
                Continue <ArrowRight size={16} />
              </button>
            )
          }
        />
      }
    >
      {step === 0 && <BriefStep content={content} />}
      {step === 1 && (
        <InvestigateStep
          content={content}
          rootIndex={rootIndex}
          tested={tested}
          onTest={(index) => setTested((prev) => new Set(prev).add(index))}
        />
      )}
      {step === 2 && <RootCauseStep content={content} rootIndex={rootIndex} />}
      {step === 3 && (
        <GeneraliseStep
          content={content}
          picked={pickedCheck}
          submitted={checkSubmitted}
          onPick={setPickedCheck}
        />
      )}
    </PlayerScaffold>
  );
}

function BriefStep({ content }: { content: DiagnosticContent }) {
  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
        Step 1 · Read the evidence
      </p>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
        The system under test
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">How this should work</h2>

      <div className="mt-5 rounded-xl border-l-4 border-amber-400 bg-white p-6 shadow-sm">
        <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
          {content.systemDescription}
        </p>
      </div>

      {content.symptoms.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
            Reported symptoms
          </p>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {content.symptoms.map((symptom, index) => (
              <div
                key={index}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <span className="flex h-6 shrink-0 items-center justify-center rounded bg-amber-400 px-1.5 text-[11px] font-bold text-slate-900">
                  E{index + 1}
                </span>
                <p className="text-sm leading-relaxed text-slate-700">{symptom}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

function InvestigateStep({
  content,
  rootIndex,
  tested,
  onTest
}: {
  content: DiagnosticContent;
  rootIndex: number;
  tested: Set<number>;
  onTest: (index: number) => void;
}) {
  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
        Step 2 · Eliminate until one holds
      </p>
      <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-brand-600">
        Test each hypothesis
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">
        Which explanation accounts for every symptom?
      </h2>

      <div className="mt-5 rounded-xl bg-slate-100/70 p-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
          Evidence on file
        </p>
        <ol className="mt-2 space-y-1 text-sm text-slate-500">
          {content.symptoms.map((symptom, index) => (
            <li key={index}>
              {index + 1}. {symptom}
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5 space-y-3">
        {content.hypotheses.map((hypothesis, index) => (
          <HypothesisCard
            key={index}
            letter={OPTION_LETTERS[index] ?? String(index + 1)}
            cause={hypothesis.cause}
            reasoning={hypothesis.reasoning}
            isRootCause={index === rootIndex}
            tested={tested.has(index)}
            onTest={() => onTest(index)}
          />
        ))}
      </div>
    </article>
  );
}

function HypothesisCard({
  letter,
  cause,
  reasoning,
  isRootCause,
  tested,
  onTest
}: {
  letter: string;
  cause: string;
  reasoning: string;
  isRootCause: boolean;
  tested: boolean;
  onTest: () => void;
}) {
  const isRoot = tested && isRootCause;

  return (
    <div
      className={`rounded-xl border bg-white p-5 shadow-sm transition ${
        isRoot ? 'border-emerald-500 ring-1 ring-emerald-500' : 'border-slate-200'
      } ${tested && !isRootCause ? 'opacity-70' : ''}`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-[11px] font-bold ${
            isRoot ? 'bg-emerald-600 text-white' : 'bg-brand-50 text-brand-700'
          }`}
        >
          {letter}
        </span>
        <p className="flex-1 text-[15px] font-semibold leading-relaxed text-slate-800">{cause}</p>
        {!tested && (
          <button
            type="button"
            onClick={onTest}
            className="shrink-0 text-xs font-bold uppercase tracking-wide text-brand-600 transition hover:text-brand-700"
          >
            Test →
          </button>
        )}
      </div>

      {tested && (
        <div className="mt-3 pl-9">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
              isRootCause ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {isRootCause && <Check size={12} />}
            {isRootCause ? 'Root cause' : 'Ruled out'}
          </span>
          <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
            {reasoning}
          </p>
        </div>
      )}
    </div>
  );
}

function RootCauseStep({ content, rootIndex }: { content: DiagnosticContent; rootIndex: number }) {
  const root = rootIndex >= 0 ? content.hypotheses[rootIndex] : undefined;

  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
        Step 3 · The root cause
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">
        {root?.cause ?? 'The confirmed root cause'}
      </h2>

      {root && (
        <div className="mt-5 rounded-xl border-l-4 border-emerald-500 bg-white p-6 shadow-sm">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
            <Check size={12} /> Root cause
          </span>
          <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
            {root.reasoning}
          </p>
        </div>
      )}

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">The fix</p>
        <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">
          {content.resolution}
        </p>
      </div>
    </article>
  );
}

function GeneraliseStep({
  content,
  picked,
  submitted,
  onPick
}: {
  content: DiagnosticContent;
  picked: number | null;
  submitted: boolean;
  onPick: (index: number) => void;
}) {
  return (
    <article>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-600">
        Step 4 · Apply it to a new case
      </p>
      <h2 className="mt-2 text-2xl font-bold text-slate-900">Same failure, different kitchen</h2>

      {content.check ? (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <CheckQuestion
            question={content.check}
            picked={picked}
            submitted={submitted}
            onPick={onPick}
          />
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="text-[15px] leading-relaxed text-slate-700">{content.resolution}</p>
        </div>
      )}
    </article>
  );
}

function CheckQuestion({
  question,
  picked,
  submitted,
  onPick
}: {
  question: QuizQuestion;
  picked: number | null;
  submitted: boolean;
  onPick: (index: number) => void;
}) {
  const correct = question.correctIndex;

  const optionState = (index: number): OptionState => {
    if (!submitted) return index === picked ? OptionState.SELECTED : OptionState.DEFAULT;
    if (index === picked) return picked === correct ? OptionState.CORRECT : OptionState.INCORRECT;
    if (index === correct) return OptionState.CORRECT;
    return OptionState.MUTED;
  };

  return (
    <Question
      question={question.question}
      options={question.options}
      optionState={optionState}
      onSelect={(index) => !submitted && onPick(index)}
      isDisabled={() => submitted}
      showLetters
      title={<p className="text-lg font-bold leading-snug text-slate-900">{question.question}</p>}
      css={{ title: '', list: 'mt-5 space-y-3', option: CHECK_OPTION_CSS }}
      feedback={
        submitted && question.feedback ? (
          <p className="mt-5 whitespace-pre-line border-t-2 border-slate-200 pt-4 text-[15px] leading-relaxed text-slate-600">
            {question.feedback}
          </p>
        ) : null
      }
    />
  );
}
